import { useMutation, useQueryClient } from 'react-query';
import { useToast } from '@razorpay/blade/components';
import api from 'api';
import { UpdateEmployeeDataGeneralSettingsInput } from 'api/gql-utils/employeeDataSettings/types';
import { getAppErrorDetails } from 'utils/AppError';
import { EMPLOYEE_DATA_GENERAL_QUERY_KEY } from './useEmployeeDataGeneralSettings';

const GENERIC_UPDATE_ERROR = 'Could not save employee data settings';

// A 422 from the monolith carries the field-level validation message (e.g.
// employee ID length / prefix length). Surface it to the user when present so
// they know exactly what to fix, otherwise fall back to the generic copy.
export const getUpdateGeneralSettingsErrorMessage = (error: unknown): string => {
  const details = getAppErrorDetails(error);
  const bodyMessage =
    details?.otherDetails?.response?.errors?.[0]?.extensions?.response?.body?.message;
  const serverMessage =
    (typeof bodyMessage === 'string' && bodyMessage.trim()) ||
    (typeof details?.otherDetails?.errorStringFromExtensionMessages === 'string' &&
      details.otherDetails.errorStringFromExtensionMessages.trim()) ||
    (details?.messageForUser?.trim() &&
      details.messageForUser !== 'Oops something went wrong!' &&
      details.messageForUser !== 'Something went wrong, please contact support.' &&
      details.messageForUser);

  return serverMessage || GENERIC_UPDATE_ERROR;
};

export const useUpdateEmployeeDataGeneralSettings = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation(
    (input: UpdateEmployeeDataGeneralSettingsInput) =>
      api.employeeDataSettings.mutations.updateEmployeeDataGeneralSettings({ input }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(EMPLOYEE_DATA_GENERAL_QUERY_KEY);
        toast.show({ content: 'Employee data settings saved', color: 'positive' });
      },
      onError: (error: unknown) => {
        toast.show({ content: getUpdateGeneralSettingsErrorMessage(error), color: 'negative' });
      },
    },
  );
};
